import { createApp, type App } from 'vue'
import { createPinia } from 'pinia'
import AppComponent from '@/App.vue'
import router from '@/router'
import { createAppVuetify } from '@/plugins/vuetify'

const STYLE_LINK_ID = 'lightning-model-page-styles'
const STYLE_LINK_ATTR = 'data-lightning-model-page-styles'

let cachedStyleNodes: Node[] | null = null
let documentStylesRelocated = false

function findAppStylesheetLinks(): HTMLLinkElement[] {
  const byMarker = document.querySelectorAll<HTMLLinkElement>(
    `link#${STYLE_LINK_ID}, link[${STYLE_LINK_ATTR}]`,
  )
  if (byMarker.length > 0) {
    return Array.from(byMarker)
  }

  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[src]'),
  )
  const bundleScript = scripts.find(script => {
    const src = script.src || ''
    return src.includes('/js/bundle.js') || src.includes('lightning-model')
  })

  if (bundleScript?.src) {
    const cssUrl = bundleScript.src.replace(/\/js\/bundle\.js(?:\?.*)?$/, '/css/styles.css')
    const match = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
    ).find(link => link.href === cssUrl || link.href.includes('/css/styles.css'))
    if (match) return [match]
  }

  return []
}

function findAppStyleTags(): HTMLStyleElement[] {
  return Array.from(document.querySelectorAll('style')).filter(style => {
    const viteId = style.getAttribute('data-vite-dev-id') || ''
    const id = style.id || ''
    return (
      Boolean(viteId) ||
      id.startsWith('vuetify-theme-stylesheet') ||
      style.hasAttribute('data-vuetify') ||
      style.hasAttribute(STYLE_LINK_ATTR)
    )
  })
}

function createStylesheetLink(href: string): HTMLLinkElement {
  const link = document.createElement('link')
  link.id = STYLE_LINK_ID
  link.setAttribute(STYLE_LINK_ATTR, '')
  link.rel = 'stylesheet'
  link.href = href
  return link
}

function resolveBundledStylesheetHref(): string | null {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[src]'),
  )
  const bundleScript = scripts.find(script => {
    const src = script.src || ''
    return src.includes('/js/bundle.js') || src.includes('lightning-model')
  })
  if (!bundleScript?.src) return null
  return bundleScript.src.replace(/\/js\/bundle\.js(?:\?.*)?$/, '/css/styles.css')
}

function collectDocumentAppStyles(): Node[] {
  const nodes: Node[] = []
  const links = findAppStylesheetLinks()

  // Prefer relocating an existing <link> (keeps font URLs resolving correctly).
  for (const link of links) {
    nodes.push(link.cloneNode(true))
    link.remove()
  }

  for (const style of findAppStyleTags()) {
    // Keep per-instance Vuetify theme tags in document until mount assigns them.
    if (style.id.startsWith('vuetify-theme-stylesheet')) continue
    nodes.push(style.cloneNode(true))
    style.remove()
  }

  if (links.length > 0) {
    return nodes
  }

  // Production embed: put a <link> inside Shadow DOM (not document.head).
  // Using <link> keeps relative font/asset URLs resolving against the CDN CSS file.
  const href = resolveBundledStylesheetHref()
  if (href) {
    nodes.push(createStylesheetLink(href))
    return nodes
  }

  // Last resort: CSS string embedded by the Vite build.
  const inlineCss = window.__LIGHTNING_MODEL_PAGE_CSS__
  if (inlineCss) {
    const style = document.createElement('style')
    style.setAttribute(STYLE_LINK_ATTR, 'inline')
    style.textContent = inlineCss
    nodes.push(style)
  }

  return nodes
}

function injectStyles(shadow: ShadowRoot): void {
  if (!cachedStyleNodes) {
    cachedStyleNodes = collectDocumentAppStyles()
    documentStylesRelocated = true
  }

  const hostStyle = document.createElement('style')
  hostStyle.textContent = `
    :host {
      display: block;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
    .lightning-model-page__root {
      display: block;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
    }
    .lightning-model-page__root,
    .lightning-model-page__root *,
    .lightning-model-page__root *::before,
    .lightning-model-page__root *::after {
      box-sizing: border-box;
    }
  `
  shadow.appendChild(hostStyle)

  for (const node of cachedStyleNodes) {
    shadow.appendChild(node.cloneNode(true))
  }
}

const VUETIFY_THEME_STYLE_ID = 'vuetify-theme-stylesheet'
let cachedThemeStyle: Node | null = null

function moveThemeStylesheet(shadow: ShadowRoot): void {
  if (!cachedThemeStyle) {
    const themeStyle = document.getElementById(VUETIFY_THEME_STYLE_ID)
    if (!themeStyle) return
    cachedThemeStyle = themeStyle.cloneNode(true)
    themeStyle.remove()
  }

  // Avoid inserting duplicates into the same shadow root.
  if (shadow.getElementById(VUETIFY_THEME_STYLE_ID)) return

  shadow.appendChild(cachedThemeStyle.cloneNode(true))
}

export function mountLightningEmbed(host: HTMLElement): App<Element> {
  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' })

  // Clear previous mount content if re-initialized.
  shadow.innerHTML = ''

  injectStyles(shadow)

  const mountEl = document.createElement('div')
  mountEl.className = 'lightning-model-page__root'
  shadow.appendChild(mountEl)

  const overlayRoot = document.createElement('div')
  overlayRoot.className = 'lightning-model-page__overlays'
  // Viewport-fixed layer so dropdown menus can use viewport coordinates reliably.
  overlayRoot.style.cssText =
    'position:fixed;inset:0;pointer-events:none;overflow:visible;z-index:10000;'
  shadow.appendChild(overlayRoot)

  const vuetify = createAppVuetify({
    attach: overlayRoot,
  })

  const app = createApp(AppComponent, {
    accountId: host.getAttribute('data-account-id'),
  })
  app.use(vuetify)
  app.use(router)
  app.use(createPinia())
  app.mount(mountEl)

  moveThemeStylesheet(shadow)

  // In case Vuetify injects the theme stylesheet slightly later.
  queueMicrotask(() => moveThemeStylesheet(shadow))

  return app
}

export function didRelocateDocumentStyles(): boolean {
  return documentStylesRelocated
}
